import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import {
  CalloutColor,
  CalloutComponent,
  CalloutPlacement,
  CalloutSize,
} from '../callout';
import { HighlightCalloutComponent } from './highlight-callout.component';
import { CalloutContentComponent } from '../callout/callout-content.component';
import { CalloutTriggerComponent } from '../callout/callout-trigger.component';

@Component({
  standalone: true,
  selector: 'fl-highlight',
  imports: [
    CalloutComponent,
    CalloutContentComponent,
    CalloutTriggerComponent,
    NgTemplateOutlet,
  ],
  template: `
    <div
      class="HighlightContent"
      [class.HasRoundCorners]="hasRoundCorners"
    >
      @if (calloutComponent) {
        <fl-callout
          #highlightCallout
          [hideCloseButton]="hideCloseButton"
          [placement]="calloutPlacement"
          [color]="calloutColor"
          [size]="calloutSize"
          [edgeToEdge]="calloutEdgeToEdge"
          [mobileCloseButton]="mobileCloseButton"
          (calloutClose)="handleCalloutClose()"
        >
          <!--
          This trigger is always [disabled] because the callout should
          only be opened programmatically via this component, not on click.
          -->
          <fl-callout-trigger
            class="HighlightCalloutTrigger"
            [disabled]="true"
          >
            <ng-container *ngTemplateOutlet="highlightContent"></ng-container>
          </fl-callout-trigger>
          <fl-callout-content>
            <ng-content select="fl-highlight-callout"></ng-content>
          </fl-callout-content>
        </fl-callout>
      } @else {
        <ng-container *ngTemplateOutlet="highlightContent"></ng-container>
      }
    </div>

    <ng-template #highlightContent>
      <ng-content></ng-content>
    </ng-template>
  `,
  styleUrls: ['./highlight.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighlightComponent implements OnChanges {
  private timeUtils = inject(TimeUtils);

  @HostBinding('class.IsHighlighted')
  @Input()
  apply = false;
  @Input() calloutPlacement = CalloutPlacement.RIGHT;
  @Input() calloutColor = CalloutColor.LIGHT;
  @Input() calloutSize = CalloutSize.SMALL;
  @Input() calloutEdgeToEdge = false;
  @Input() hideCloseButton = true;
  @Input() mobileCloseButton = true;
  @Input() hasRoundCorners = false;

  @Output() calloutClose = new EventEmitter<void>();

  @ViewChild('highlightCallout')
  highlightCallout: CalloutComponent;

  @ContentChild(HighlightCalloutComponent, { static: true })
  calloutComponent: HighlightCalloutComponent;

  // FIXME: T300116 Enabling hydration errors on the webapp server
  // TypeError: Cannot read properties of null (reading 'parentElement')
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  ngOnChanges(changes: SimpleChanges): void {
    if ('apply' in changes && this.calloutComponent) {
      this.timeUtils.setTimeout(() => {
        if (!this.highlightCallout) {
          return;
        }

        if (this.apply) {
          this.highlightCallout.open();
        } else {
          this.highlightCallout.close();
        }
      });
    }
  }

  handleCalloutClose(): void {
    this.calloutClose.emit();
  }

  @HostListener('click')
  onClick(): void {
    // We should hide the callout whenever the user
    // clicked outside and apply is true.
    if (this.apply && this.highlightCallout) {
      this.highlightCallout.close();
    }
  }
}
